"use client"

import { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import {
  useCreateProduct,
  useUpdateProduct,
  useStore,
  type Product,
  type ProductCategory,
} from "@/api/store"
import { ImageIcon, PlusIcon, XIcon } from "lucide-react"

interface ProductDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  product?: Product | null
  categories: ProductCategory[]
}

function PhotoUpload({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (base64: string) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => onChange(ev.target?.result as string)
    reader.readAsDataURL(file)
  }
  return (
    <div>
      <p className="text-sm font-medium mb-1">{label}</p>
      <div
        className="relative w-full cursor-pointer overflow-hidden rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 hover:bg-muted/50 transition-colors"
        style={{ aspectRatio: "4/3" }}
        onClick={() => ref.current?.click()}
      >
        {value ? (
          <img src={value} alt="preview" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs">Click to upload</span>
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  )
}

export function ProductDialog({
  open,
  onOpenChange,
  product,
  categories,
}: ProductDialogProps) {
  const { toast } = useToast()
  const { data: store } = useStore()
  const currency = store?.currency ?? "ALL"
  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [price, setPrice] = useState("")
  const [categoryId, setCategoryId] = useState("")
  const [available, setAvailable] = useState(true)
  const [mainPhoto, setMainPhoto] = useState("")
  const [extraPhotos, setExtraPhotos] = useState<string[]>([])
  const [availabilityMode, setAvailabilityMode] = useState<string>("")
  const [availabilityTime, setAvailabilityTime] = useState("")

  const isEditing = !!product

  useEffect(() => {
    if (open) {
      if (product) {
        setTitle(product.title)
        setDescription(product.description ?? "")
        setPrice(String(product.price))
        setCategoryId(product.categoryId ?? "")
        setAvailable(product.available)
        setMainPhoto(product.mainPhoto)
        setExtraPhotos(product.extraPhotos ?? [])
        setAvailabilityMode(product.availabilityMode ?? "")
        setAvailabilityTime(product.availabilityTime ?? "")
      } else {
        setTitle("")
        setDescription("")
        setPrice("")
        setCategoryId("")
        setAvailable(true)
        setMainPhoto("")
        setExtraPhotos([])
        setAvailabilityMode("")
        setAvailabilityTime("")
      }
    }
  }, [open, product])

  const addExtraPhoto = (base64: string) => {
    setExtraPhotos((prev) => [...prev, base64])
  }

  const removeExtraPhoto = (index: number) => {
    setExtraPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!mainPhoto) {
      toast({ title: "Please upload a main photo", variant: "destructive" })
      return
    }

    const priceNum = parseFloat(price)
    if (isNaN(priceNum) || priceNum < 0) {
      toast({ title: "Invalid price", variant: "destructive" })
      return
    }

    const payload = {
      title,
      description: description || undefined,
      mainPhoto,
      extraPhotos,
      categoryId: categoryId || undefined,
      price: priceNum,
      available,
      availabilityMode: availabilityMode || undefined,
      availabilityTime: availabilityMode && availabilityTime ? availabilityTime : undefined,
    }

    try {
      if (isEditing && product) {
        await updateProduct.mutateAsync({ id: product._id, data: payload })
        toast({ title: "Product updated" })
      } else {
        await createProduct.mutateAsync(payload)
        toast({ title: "Product created" })
      }
      onOpenChange(false)
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" })
    }
  }

  const isPending = createProduct.isPending || updateProduct.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Product" : "Add Product"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PhotoUpload label="Main Photo *" value={mainPhoto} onChange={setMainPhoto} />

          <div className="space-y-1">
            <Label>Extra Photos (gallery)</Label>
            <div className="grid grid-cols-3 gap-2">
              {extraPhotos.map((photo, i) => (
                <div key={i} className="relative">
                  <img src={photo} alt="" className="h-24 w-full rounded-md object-cover border" />
                  <button
                    type="button"
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-destructive text-white p-0.5"
                    onClick={() => removeExtraPhoto(i)}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {extraPhotos.length < 6 && (
                <label className="flex h-24 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed text-muted-foreground hover:bg-muted/30">
                  <PlusIcon className="h-5 w-5" />
                  <span className="text-xs mt-1">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = (ev) => addExtraPhoto(ev.target?.result as string)
                      reader.readAsDataURL(file)
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Protein shake"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Product details..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="price">Price *</Label>
              <div className="flex">
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0"
                  required
                  className="rounded-r-none"
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 bg-muted px-3 text-sm text-muted-foreground font-medium">
                  {currency}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="No category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <Label className="cursor-pointer">Available for ordering</Label>
            <Switch checked={available} onCheckedChange={setAvailable} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Time availability</Label>
              <Select
                value={availabilityMode || "always"}
                onValueChange={(v) => {
                  setAvailabilityMode(v === "always" ? "" : v)
                  if (v === "always") setAvailabilityTime("")
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Always available</SelectItem>
                  <SelectItem value="before">Before time</SelectItem>
                  <SelectItem value="after">After time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {availabilityMode && (
              <div className="space-y-1">
                <Label htmlFor="avail-time">
                  {availabilityMode === "before" ? "Before" : "After"}
                </Label>
                <Input
                  id="avail-time"
                  type="time"
                  value={availabilityTime}
                  onChange={(e) => setAvailabilityTime(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : isEditing ? "Save changes" : "Create product"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
